import React, { useMemo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useLocalizedPath } from '../../routes/useLanguageSync';
import { useSEO } from '../../hooks/useSEO';
import { useMarkdownContent } from '../../hooks/useMarkdownContent';
import { buildBreadcrumbJsonLd } from '../../config/seo';
import { getServiceBySlug } from '../../data/services';
import { useCaseStudyIndex } from '../../hooks/useCaseStudyIndex';
import { Language } from '../../routes/routeConfig';
import BlobDecoration from '../../components/BlobDecoration/BlobDecoration';
import VideoGallery from '../../components/VideoGallery/VideoGallery';
import AppScreenshots from '../../components/AppScreenshots/AppScreenshots';
import FAQ from '../../components/FAQ/FAQ';
import styles from './ServiceDetail.module.css';

const blobColors: Record<string, 'red' | 'green' | 'blue' | 'yellow'> = {
  rehab: 'yellow',
  menhely: 'blue',
  gazdi: 'green',
};

const ServiceDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const getPath = useLocalizedPath();
  const service = slug ? getServiceBySlug(slug) : undefined;
  const { content: markdownContent, loading: markdownLoading } = useMarkdownContent(slug || '');
  const { slugs: caseStudySlugs } = useCaseStudyIndex(slug || '');
  const hasCaseStudies = caseStudySlugs.length > 0;

  const language = i18n.language.split('-')[0];
  const heroShot = service?.screenshots?.[0];
  const heroShotSrc = heroShot ? heroShot.src[language] ?? heroShot.src.hu : null;

  const serviceSpecificTitleKey = service ? `seo.serviceDetail.${service.slug}.title` : '';
  const hasSpecificTitle = service && i18n.exists(serviceSpecificTitleKey);

  const seo = useSEO({
    titleKey: hasSpecificTitle ? serviceSpecificTitleKey : 'seo.serviceDetail.title',
    descriptionKey: 'seo.serviceDetail.description',
    routeKey: 'serviceDetail',
    routeParams: { slug },
    titleValues: hasSpecificTitle ? undefined : { serviceName: service ? t(service.nameKey) : '' },
    descriptionValues: { serviceDescription: service ? t(service.shortDescriptionKey) : '' },
    structuredData: service
      ? buildBreadcrumbJsonLd(
          [
            { name: t('nav.home'), routeKey: 'home' },
            { name: t('nav.services'), routeKey: 'services' },
            { name: t(service.nameKey), routeKey: 'serviceDetail', params: { slug: service.slug } },
          ],
          i18n.language as Language
        )
      : undefined,
  });

  const sections = useMemo(() => {
    if (!markdownContent) return [];
    const parts = markdownContent.split(/^(?=## )/m).filter((p) => p.trim().length > 0);
    return parts.map((part) => {
      const imageMatch = part.match(/!\[(.*?)\]\((.*?)\)\n*/);
      const text = imageMatch ? part.replace(imageMatch[0], '') : part;
      return {
        text,
        imageSrc: imageMatch?.[2] || null,
        imageAlt: imageMatch?.[1] || '',
      };
    });
  }, [markdownContent]);

  const markdownComponents = useMemo(() => ({
    h2: ({ children }: any) => <h2 className={styles.sectionTitle}>{children}</h2>,
    p: ({ node, children }: any) => {
      const childNodes = node?.children;
      if (childNodes?.length === 1 && childNodes[0].tagName === 'a') {
        return <div className={styles.ctaParagraph}>{children}</div>;
      }
      return <p className={styles.paragraph}>{children}</p>;
    },
    a: ({ href, children }: any) => {
      if (href?.startsWith('/')) {
        return <Link to={href}>{children}</Link>;
      }
      return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    },
    ul: ({ children }: any) => <ul className={styles.list}>{children}</ul>,
    li: ({ children }: any) => <li className={styles.listItem}>{children}</li>,
  }), []);

  if (!service) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className={styles.detail}>
      {seo}
      <section className={`${styles.header} ${heroShotSrc ? styles.headerTall : ''}`}>
        <BlobDecoration
          color={blobColors[service.slug] ?? 'blue'}
          position="top-right"
          size="lg"
        />
        {heroShotSrc && (
          <img src={heroShotSrc} alt="" aria-hidden="true" className={styles.heroPhone} />
        )}
        <div className="container">
          <div className={styles.headerContent}>
            <img src={service.logo} alt={t(service.nameKey)} className={styles.logo} />
            {service.webAppUrl && service.webAppKind === 'public' && (
              <div className={styles.heroCta}>
                <a
                  href={service.webAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.webAppButton}
                  style={{ backgroundColor: service.accentColor }}
                >
                  {t('services.webApp.openPublic')}
                </a>
                <p className={styles.webAppHint}>{t('services.webApp.openPublicHint')}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className={styles.descriptionSection}>
        <div className="container">
          {markdownLoading ? null : sections.length > 0 ? (
            sections.map((section, index) => {
              const imageLeft = index % 2 === 0;

              // App screenshots already carry a device bezel, so they must not be
              // mirrored by the decorative scaleX(-1) applied to photos.
              const isAppScreenshot = section.imageSrc?.startsWith('/content/images/apps/');

              return section.imageSrc ? (
                <div
                  key={index}
                  className={`${styles.section} ${styles.sectionWithImage} ${imageLeft ? '' : styles.sectionImageRight}`}
                >
                  <div
                    className={`${styles.sectionImageWrapper} ${isAppScreenshot ? styles.phoneWrapper : ''}`}
                  >
                    <img
                      src={section.imageSrc}
                      alt={section.imageAlt}
                      className={`${styles.sectionImage} ${isAppScreenshot ? styles.phoneImage : ''}`}
                    />
                  </div>
                  <div className={styles.sectionText}>
                    <ReactMarkdown components={markdownComponents}>{section.text}</ReactMarkdown>
                  </div>
                </div>
              ) : (
                <div key={index} className={styles.section}>
                  <ReactMarkdown components={markdownComponents}>{section.text}</ReactMarkdown>
                </div>
              );
            })
          ) : (
            <div className={styles.descriptionContent}>
              <p className={styles.paragraph}>{t(service.descriptionKey)}</p>
            </div>
          )}
        </div>
      </section>

      <section className={styles.featuresSection}>
        <div className="container">
          <h2 className={styles.featuresTitle}>{t('services.featuresTitle')}</h2>
          <ul className={styles.featuresList}>
            {service.featuresKeys.map((key, index) => (
              <li key={index} className={styles.featureItem}>
                <span
                  className={styles.featureDot}
                  style={{ backgroundColor: service.accentColor }}
                />
                {t(key)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {service.screenshots && service.screenshots.length > 0 && (
        <AppScreenshots screenshots={service.screenshots} accentColor={service.accentColor} />
      )}

      {service.videos && service.videos.length > 0 && (
        <VideoGallery videos={service.videos} accentColor={service.accentColor} />
      )}

      <section className={styles.ctaSection}>
        <div className="container">
          {hasCaseStudies && (
              <Link
                  to={getPath('caseStudies', { slug: service.slug })}
                  className={styles.caseStudiesLink}
                  style={{ color: service.accentColor, borderColor: service.accentColor }}
              >
                {t('caseStudies.viewCaseStudies')}
              </Link>
          )}
          <Link
              to={getPath('contact', { query: { service: service.slug } })}
              className={styles.ctaButton}
              style={{ backgroundColor: service.accentColor }}
          >
            {t('services.contactCta')}
          </Link>
          {service.webAppUrl && (
            <p className={styles.webAppNote}>
              <a
                href={service.webAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.webAppLink}
                style={{ color: service.accentColor }}
              >
                {t(
                  service.webAppKind === 'public'
                    ? 'services.webApp.openPublic'
                    : 'services.webApp.signIn'
                )}
              </a>
              <span className={styles.webAppNoteHint}>
                {t(
                  service.webAppKind === 'public'
                    ? 'services.webApp.openPublicHint'
                    : 'services.webApp.signInHint'
                )}
              </span>
            </p>
          )}
        </div>
      </section>

      <FAQ slug={service.slug} accentColor={service.accentColor} />
    </div>
  );
};

export default ServiceDetail;
